import React from 'react';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';

import store from 'store/Store';
import Actions from 'Actions';
import UserActions from 'actions/UserActions';
import Location from 'utils/Location';
import anaUtils from 'analytics/utils';
import anaConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import ForgotPasswordBindings from 'analytics/bindingMethods/components/globalModals/forgotPasswordModal/ForgotPasswordBindings';
import {
    Text, Button, Link, Image, Box
} from 'components/ui';
import Modal from 'components/Modal/Modal';
import InputEmail from 'components/Inputs/InputEmail/InputEmail';
import ErrorList from 'components/ErrorList';
import InfoButton from 'components/InfoButton/InfoButton';
import mediaUtils from 'utils/Media';
import localeUtils from 'utils/LanguageLocale';
import linkTrackingError from 'analytics/bindings/pages/all/linkTrackingError';
import ForgotPasswordModalEN from 'components/GlobalModals/ForgotPasswordModal/locales/ForgotPasswordModal_en_US';
import ForgotPasswordModalFR from 'components/GlobalModals/ForgotPasswordModal/locales/ForgotPasswordModal_fr_CA';
import { mediaQueries } from 'style/config';
import passkeysUtils from 'utils/passkeys';
const { Media } = mediaUtils;

const POS_ACCOUNT_ERROR = 'ERR_400004';
const { getLocalisedDataFromResource } = localeUtils;

class ForgotPasswordModal extends BaseClass {
    constructor(props) {
        super(props);
        this.state = {
            isOpen: false,
            errorMessages: this.props.messages,
            isPasskeyEnabled: false
        };

        this.loginInput = null;
    }

    async componentDidMount() {
        const isEnabledEntryFlag = Sephora.configurationSettings?.passkey?.optInResetPwd?.isEnabled;
        const isDisabled = await passkeysUtils.isPasskeyDisabled(isEnabledEntryFlag);

        this.setState({ isPasskeyEnabled: !isDisabled });
    }

    requestClose = () => {
        store.dispatch(Actions.showForgotPasswordModal(false));
    };

    isValid = () => {
        const loginError = this.loginInput.validateError();

        //Analytics
        const fieldsToCheck = [loginError];
        const errors = fieldsToCheck.filter(value => !!value);

        if (errors.length) {
            processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                data: {
                    bindingMethods: linkTrackingError,
                    eventStrings: ['event140', 'event141'],
                    fieldErrors: errors,
                    ...anaUtils.getLastAsyncPageLoadData()
                }
            });
        } //End analytics

        return fieldsToCheck.filter(value => !!value).length === 0;
    };

    dispatchShowInfoModal = errorMsg => {
        const getText = getLocalisedDataFromResource(ForgotPasswordModalEN, ForgotPasswordModalFR);

        if (errorMsg) {
            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: getText('resetPassword'),
                    message: errorMsg,
                    buttonText: getText('confirmButton'),
                    bodyPaddingBottom: 4,
                    isHtml: true
                })
            );
        } else {
            const email = this.loginInput.getValue();
            store.dispatch(Actions.showResetPasswordConfirmationModal({ isOpen: true, email }));
        }

        this.requestClose();
    };

    handleCreatePasskey = () => {
        if (!this.isValid()) {
            return;
        }

        const email = this.loginInput.getValue();
        const getText = getLocalisedDataFromResource(ForgotPasswordModalEN, ForgotPasswordModalFR);
        const onSuccess = () => {
            store.dispatch(
                Actions.showResetPasswordConfirmationModal({
                    isOpen: true,
                    email,
                    isPasskeyFlow: true
                })
            );
            this.requestClose();
        };

        const onFailure = response => {
            const errorMsg = response.errorMessage || (response.errors || [])[0]?.errorMessage || getText('fallbackPasskeyErrorMsg');
            this.dispatchShowInfoModal(errorMsg);
        };

        store.dispatch(UserActions.forgotPassword(email, onSuccess, onFailure, null, { pk: true }));
    };

    showPasskeyInfo = () => {
        store.dispatch(
            Actions.showPasskeysInfoModal({
                isOpen: true
            })
        );
    };

    submit = () => {
        const email = this.loginInput.getValue();
        const getText = getLocalisedDataFromResource(ForgotPasswordModalEN, ForgotPasswordModalFR);

        const onSuccess = () => {
            this.dispatchShowInfoModal();
            ForgotPasswordBindings.emailSent({ emailId: email });
            anaUtils.setNextPageData({ events: ['event140', 'event100'] });
        };

        const onFailure = async response => {
            const errorMsg = response.errorMessage || (response.errors || [])[0]?.errorMessage || getText('fallbackErrorMsg');
            const errorCode = (response.errors || [])[0]?.errorCode;

            if (errorCode === POS_ACCOUNT_ERROR) {
                store.dispatch(Actions.showCheckYourEmailModal({ isOpen: true, email, isResetPasswordFlow: true }));
            } else {
                this.dispatchShowInfoModal(errorMsg);
            }

            if (response.errorMessages) {
                processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                    data: {
                        bindingMethods: await import('analytics/bindings/pages/all/linkTrackingError'),
                        errorMessages: response.errorMessages,
                        eventStrings: ['event140', 'event141'],
                        ...anaUtils.getLastAsyncPageLoadData()
                    }
                });
            }
        };

        if (this.isValid()) {
            const source = Location.isOrderConfirmationPage() ? 'orderConfirmation' : null;
            store.dispatch(UserActions.forgotPassword(email, onSuccess, onFailure, source));
        }
    };

    render() {
        const getText = localeUtils.getLocalisedDataFromResource(ForgotPasswordModalEN, ForgotPasswordModalFR);
        const { isPasskeyEnabled } = this.state;

        return (
            <Modal
                isOpen={this.props.isOpen}
                onDismiss={this.requestClose}
                width={1}
            >
                <Modal.Header
                    hasBorder={!isPasskeyEnabled}
                    paddingTop={isPasskeyEnabled ? '50px' : null}
                    isLeftAligned={isPasskeyEnabled}
                >
                    {isPasskeyEnabled && (
                        <>
                            <Media lessThan='md'>
                                <Image
                                    display='block'
                                    height={96}
                                    marginBottom={4}
                                    margin='0'
                                    src='/img/ufe/passkey-modal-image.svg'
                                    width={96}
                                />
                            </Media>
                            <Media greaterThanOrEqual='md'>
                                <Image
                                    display='block'
                                    height={64}
                                    marginBottom={4}
                                    marginX='0'
                                    src='/img/ufe/passkey-modal-image-desktop.svg'
                                    width={64}
                                />
                            </Media>
                        </>
                    )}
                    <Modal.Title>{getText(isPasskeyEnabled ? 'forgotYourPasswordTitle' : 'modalTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body
                    lineHeight='tight'
                    paddingTop={isPasskeyEnabled ? 0 : null}
                >
                    {isPasskeyEnabled && (
                        <>
                            <Text
                                is='p'
                                marginBottom={3}
                            >
                                {getText('passkeysIntroMessage')}
                                <InfoButton
                                    marginLeft={0}
                                    onClick={this.showPasskeyInfo}
                                />
                            </Text>

                            <Text
                                is='p'
                                marginBottom={3}
                            >
                                {getText('verifyEmailMessage')}
                            </Text>
                        </>
                    )}

                    {!isPasskeyEnabled && (
                        <Text
                            is='p'
                            marginBottom={3}
                        >
                            {getText('resetPasswordMessage')}
                        </Text>
                    )}

                    <form
                        noValidate
                        onSubmit={e => {
                            e.preventDefault();
                            this.submit();
                        }}
                    >
                        <ErrorList errorMessages={this.state.errorMessages} />

                        <InputEmail
                            label={getText('emailLabel')}
                            id='forgotAnswer_email'
                            login={this.props.presetLogin}
                            ref={c => {
                                if (c !== null) {
                                    this.loginInput = c;
                                }
                            }}
                            hideAsterisk={true}
                        />

                        {isPasskeyEnabled && (
                            <>
                                <Text
                                    is='p'
                                    color='gray'
                                    fontSize='sm'
                                    marginTop={2}
                                >
                                    {getText('sharedDeviceDisclaimer')}
                                </Text>

                                <Text
                                    is='p'
                                    color='gray'
                                    fontSize='sm'
                                    marginTop={3}
                                >
                                    {getText('termsAndConditions')}
                                    <Link
                                        color='blue'
                                        underline={true}
                                        href='/beauty/terms-of-use'
                                    >
                                        {getText('termsOfUse')}
                                    </Link>
                                    {getText('termsAndConditionsRest')}
                                    <Link
                                        color='blue'
                                        underline={true}
                                        href='/beauty/privacy-policy'
                                    >
                                        {getText('privacyPolicy')}
                                    </Link>
                                    .
                                </Text>

                                <Box
                                    display='flex'
                                    gap={16}
                                    marginTop={16}
                                    justifyContent='space-between'
                                    css={{
                                        [mediaQueries.mdMax]: {
                                            flexDirection: 'column-reverse'
                                        }
                                    }}
                                >
                                    <Button
                                        variant='secondary'
                                        type='submit'
                                    >
                                        {getText('resetMyPasswordCta')}
                                    </Button>
                                    <Button
                                        variant='primary'
                                        type='button'
                                        onClick={this.handleCreatePasskey}
                                    >
                                        {getText('createNewPasskeyCta')}
                                    </Button>
                                </Box>
                            </>
                        )}

                        {!isPasskeyEnabled && (
                            <Button
                                variant='primary'
                                type='submit'
                                marginTop={3}
                            >
                                {getText('sendEmailButton')}
                            </Button>
                        )}
                    </form>

                    <Text
                        is='p'
                        color='gray'
                        marginTop={5}
                        fontSize='sm'
                    >
                        <b>{getText('stillHavingTroublesMsg')}</b>
                        <br />
                        {getText('unableResetPasswordMessage')}{' '}
                        <Link
                            color='blue'
                            underline={true}
                            href='tel:1-877-737-4672'
                        >
                            {getText('sephoraPhoneNumber')}
                        </Link>{' '}
                        {getText('phoneNumberTTY')}
                        {' | '}
                        {getText('forAssistanceMessage')}{' '}
                        <Link
                            color='blue'
                            underline={true}
                            href='/beauty/accessibility'
                        >
                            {getText('accessibility')}
                        </Link>
                        .
                    </Text>
                </Modal.Body>
            </Modal>
        );
    }
}

export default wrapComponent(ForgotPasswordModal, 'ForgotPasswordModal', true);
